import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { get, sumBy, isNil } from 'lodash';
import { useParams } from 'react-router-dom';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import { TooltipHost, DirectionalHint, Stack, Spinner, SpinnerSize } from 'office-ui-fabric-react';
import { Pivot, PivotItem, PivotLinkSize, PivotLinkFormat } from 'office-ui-fabric-react/lib/Pivot';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { can } from '../../utils/auth';
import Link from '../../components/Link';
import Main from '../../components/Main';
import Module from '../../components/Module';
import ModuleContent from '../../components/ModuleContent';
import Card from '../../components/Card';
import Header from '../../components/Header';
import SubModuleHeader from '../../components/SubModuleHeader';
import SubModule from '../../components/SubModule';
import ModuleHeader from '../../components/ModuleHeader/ModuleHeader';
import SubModuleContent from '../../components/SubModuleContent';
import ComparisonTable from '../../components/ComparisonTable';
import PageTitle from '../../components/PageTitle';
import StatementDetailsActionRequired from '../../components/StatementDetailsActionRequired';
import { pxToRem, formatDateString, formatNumberWithCommas } from '../../utils';
import getStatementPaymentStatusInfo from './StatementPaymentStatusInfo';
import {
  fetchStatementDetails,
  fetchStatementActivity,
  fetchAttachment,
  deleteStatementAttachment,
  removeAttachment,
} from '../../actions/statements';
import { fetchTasks } from '../../actions/tasks';
import ContentModuleTab from '../../components/ContentModuleTab';
import VerticalSteps from '../../components/VerticalSteps';
import FinancialTable from '../../components/FinancialTable';
import FinancialTableHeader from '../../components/FinancialTableHeader';
import FinancialTableBody from '../../components/FinancialTableBody';
import FinancialTableBodyHeader from '../../components/FinancialTableBodyHeader';
import FinancialTableFooter from '../../components/FinancialTableFooter';
import StackGrid from '../../components/StackGrid';
import ContactModule from '../../components/ContactModule';
import DocumentsList from '../../components/DocumentsList';
import theme, { breakpoints } from '../../theme.style';
import styles from './Statement.style';
import StatementSummary from '../../components/StatementSummary';
import useAPI from '../../hooks/useAPI';
import PaymentStatusInfoItem from '../../components/PaymentStatusInfoItem';
import StatementTieringAndFeeStructure from '../../components/StatementTieringAndFeeStructure';

const Statement = () => {
  const dispatch = useDispatch();
  const [workFlowSteps, setWorkFlowSteps] = useState([]);
  const { id, partnerAddress } = useParams();
  const [isPaymentSent, setIsPaymentSent] = useState(false);
  const [paymentStatusInfoDetails, setPaymentStatusInfoDetails] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [paymentReferenceDetails, setPaymentReferenceDetails] = useState(null);
  const { width } = useWindowDimensions();
  const isSmallScreen = width <= breakpoints.lg;
  const statementDetail = useSelector(state => get(state, `entities.statementDetail.${id}`, null));
  const statementActivity = useSelector(state =>
    get(state, `entities.statementActivity.${id}`, null),
  );

  const partner = get(statementDetail, 'partner', null);
  // const partnerAddress = get(statementDetail, 'partner.address', null);
  const contract = get(statementDetail, 'contract', null);
  const contractAddress = get(statementDetail, `contract.address`, null);
  const statementFrequency = get(statementDetail, 'contract.statementFrequency', null);
  const payment = get(statementDetail, 'payment', null);
  const statementStartDate = get(statementDetail, 'statementStartDate', null);
  const statementEndDate = get(statementDetail, 'statementEndDate', null);
  const transactionPeriodCutOff = get(statementDetail, 'transactionPeriodCutOff', null);
  const allAttachments = get(statementDetail, 'attachments', []);
  const workFlow = get(statementActivity, 'workFlow', null);
  const comments = get(statementDetail, 'comments', null);
  const perCapDetails = get(statementDetail, 'perCapDetails', null);
  const contractAdjustments = get(statementDetail, 'contractAdjustments', null);
  const tieringBreakdown = get(statementDetail, 'tieringBreakdown', null);

  const statementDetailsAPI = useAPI(
    () => fetchStatementDetails(partnerAddress, id),
    [partnerAddress, id],
  );

  let remappedAttachments = [];
  if (allAttachments && allAttachments.length > 0) {
    const evidence = allAttachments.filter(x =>
      ['evidence', 'executiveApproval', 'reconResults'].includes(x.type),
    );
    remappedAttachments = evidence.map((x, index) => {
      return {
        key: index,
        downloadUrl: `${window.config.sapiUrl}/partner/${partner.address}/statements/${id}/download?documentName=${x.fileName}`,
        documentLocation: `${x.containerName}/${x.fileName}`,
        documentName: x.displayName,
        documentType: x.type,
        fileName: x.fileName,
        documentUploadTimeStamp: x.uploadTimeStamp
          ? moment.tz(x.uploadTimeStamp, 'America/Los_Angeles').format('MMM DD, YYYY HH:mmA z')
          : undefined,
        notRemovableFile: x.notRemovableFile,
      };
    });
  }

  const handleDownload = (item, open) => {
    dispatch(fetchAttachment(item.downloadUrl, open));
  };

  const handleDelete = item => {
    dispatch(deleteStatementAttachment(id, partnerAddress, item.fileName)).then(result => {
      if (result && !result.error) {
        dispatch(removeAttachment(id, item.fileName));
      }
    });
  };

  const roundedSum = perCapDetailsArray => {
    const totalAmount = sumBy(
      perCapDetailsArray,
      item => item?.currentStatementPeriod?.amountReturned,
    );

    return totalAmount && totalAmount.toFixed(2);
  };

  const canViewStatementActivity = can('viewStatementActivity');
  const canViewStatementAttachments = can('viewStatementAttachments');
  const canDeleteStatementAttachments = can('deleteStatementAttachment');

  const tasks = useSelector(state => Object.values(get(state, 'entities.tasks', {}))).filter(
    task => task.area === 'statements' && task.resourceId === id,
  );

  const isLastStep =
    statementActivity?.workFlow.filter(s => s.assignedTo !== 'system').slice(-1)[0]?.taskStatus ===
    'Open';

  const isStepBeforeBAM =
    statementActivity?.workFlow.find(s => s.taskStatus === 'Future')?.assignedTo ===
    'businessAccountManager';

  const breakdownMapping = pay => ({
    amount: pay.amount,
    currency: pay.currency,
    name: pay.title || pay.name,
    address: pay.type,
    details: pay.details,
    parenthesizeIfNegative: true,
    bold: pay.bold,
  });

  useEffect(() => {
    if (can('viewTasks')) {
      dispatch(fetchTasks(['statements']));
    }
  }, [dispatch, partnerAddress, id]);

  useEffect(() => {
    if (id && can('viewWorkflows')) {
      dispatch(fetchStatementActivity(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    const steps = [];

    if (workFlow != null) {
      workFlow.forEach(taskStep => {
        const step = {};
        if (taskStep) {
          step.status = taskStep.taskStatus;
          step.content = taskStep.workflowStepText;
          step.additionalContent = taskStep.workflowStepAdditionalText;
          step.date = taskStep.lastUpdatedTimestamp;
        }
        if (payment) {
          step.amount = payment.amount;
        }
        steps.push(step);
      });
    }
    setWorkFlowSteps(steps);
  }, [workFlow, payment]);

  useEffect(() => {
    if (payment) {
      const {
        primaryStatus,
        secondaryStatus,
        paymentDate,
        isPaymentCarriedOver,
        invoiceId,
        paymentDueDate,
        paymentReferenceNumber,
      } = payment;

      // TODO: This may need to change. Do we need to abstract a utility
      // method like this? and we need to not use `isPartner`
      const statementDetailsInfo = getStatementPaymentStatusInfo(
        primaryStatus,
        secondaryStatus,
        payment.primaryStatus === 'Not Available' ? paymentDueDate : paymentDate,
        isPaymentCarriedOver,
        invoiceId || null,
        paymentReferenceNumber || null,
        !canViewStatementActivity,
      );

      // TODO: Stop using local state for this
      setIsPaymentSent(primaryStatus === 'PaymentSent');
      const { paymentStatusInfo, invoice, paymentReference } = statementDetailsInfo;
      setPaymentStatusInfoDetails(paymentStatusInfo);
      setInvoiceDetails(invoice);
      setPaymentReferenceDetails(paymentReference);
    }
  }, [payment, canViewStatementActivity]);

  if (statementDetailsAPI.isLoading) {
    return (
      <div className={styles.spinner}>
        <Spinner size={SpinnerSize.medium} />
      </div>
    );
  }

  if (statementDetailsAPI.error) {
    return (
      <Stack className={styles.errorContainer}>
        <Stack.Item>
          <Text as="h1">Error loading statement</Text>
        </Stack.Item>
        <Stack.Item>
          <Text as="p" className={styles.errorMessage}>
            {statementDetailsAPI.error.message}
          </Text>
        </Stack.Item>
      </Stack>
    );
  }

  const renderStatementRoyaltiesEarnedPivotItem = () => {
    if (!statementDetail) return null;

    const itemCount = !isNil(statementDetail?.totalRoyalty?.totalRoyaltyEarnedCC)
      ? `${formatNumberWithCommas(statementDetail.totalRoyalty.totalRoyaltyEarnedCC.toFixed(2))} ${
          statementDetail.payment.currency
        }`
      : `${formatNumberWithCommas(Number(0.0).toFixed(2))} ${statementDetail.payment.currency}`;

    return (
      <PivotItem
        headerText="Royalties Earned"
        itemCount={itemCount}
        isSmallScreen={isSmallScreen}
        onRenderItemLink={ContentModuleTab}
      >
        <FinancialTable>
          <FinancialTableHeader>Royalties Earned</FinancialTableHeader>
          {statementDetail?.config && statementDetail?.summary ? (
            <div className={styles.statementSummaryContainer}>
              {/* Summary table for contracts with a statement configuration */}
              <StatementSummary config={statementDetail.config} summary={statementDetail.summary} />
            </div>
          ) : (
            <div className={styles.statementSummaryContainer}>
              {/* Default summary table for contracts without a statement configuration */}
              <StatementSummary
                config={{
                  version: 1,
                  summary: {
                    projection: [
                      {
                        to: 'Royalty Type',
                      },
                      {
                        to: 'Net Rev',
                        format: {
                          type: 'number',
                          maxDecimals: 2,
                          showTotals: true,
                        },
                      },
                      {
                        to: 'Units',
                        format: {
                          type: 'number',
                          showTotals: true,
                        },
                      },
                      {
                        to: 'Royalties Earned',
                        format: {
                          type: 'number',
                          maxDecimals: 2,
                          showTotals: true,
                        },
                      },
                    ],
                  },
                }}
                summary={statementDetail?.revenueBreakdown?.map(revenue => {
                  return {
                    'Royalty Type': `${revenue.rate}% of ${revenue.type}`,
                    'Royalties Earned': revenue.totalRoyaltyEarnedCC,
                    'Net Rev': revenue.totalNetRevenueCC,
                    Units: revenue.quantity,
                  };
                })}
              />
            </div>
          )}
        </FinancialTable>
      </PivotItem>
    );
  };

  const renderStatementPaymentBreakdownPivotItem = () => {
    if (!statementDetail) return null;
    const paymentCurrency =
      statementDetail?.paymentCurrency ??
      statementDetail?.contractCurrency ??
      statementDetail?.contract?.currency;
    const itemCount =
      statementDetail?.payment.amount && paymentCurrency
        ? `${formatNumberWithCommas(statementDetail.payment.amount.toFixed(2))} ${paymentCurrency}`
        : `0.00 ${paymentCurrency}`;

    return (
      <PivotItem
        headerText="Payment"
        itemCount={itemCount}
        isSmallScreen={isSmallScreen}
        onRenderItemLink={ContentModuleTab}
      >
        <FinancialTable>
          <FinancialTableHeader>Payment</FinancialTableHeader>
          <FinancialTableBody
            items={statementDetail?.paymentBreakdown?.map(breakdownMapping)}
            collapsibleSubLineItems
          />
        </FinancialTable>

        <FontIcon iconName="Info" ariaLabel="Info" className={styles.infoIcon} />
        <Text as="p" variant="small" className={styles.infoText}>
          Payment may be subject to withholding tax
        </Text>
      </PivotItem>
    );
  };

  return (
    statementDetail && (
      <>
        <Header>
          <div className={styles.breadcrumbs}>
            <Link href={`/partners/${partnerAddress}`}>{partner.name}</Link>
            <span className={styles.slash}> / </span>
            <Link href={`/contracts/${contractAddress}`}>{contract.name}</Link>
          </div>

          <PageTitle className={styles.frequency}>
            {statementDetail?.status === 'Preview'
              ? `Monthly Preview Statement`
              : `${statementFrequency.charAt(0).toUpperCase()}${statementFrequency.slice(
                  1,
                )}ly Statement`}
          </PageTitle>

          <StackGrid
            smCols={1}
            mdCols={2}
            lgCols={2}
            gap={8}
            parentClassName={styles.meta}
            childClassName={styles.metaChild}
          >
            <div>
              <Text className={styles.statementPeriod}>
                {formatDateString(statementStartDate, 'MMM d, yyyy')} -{' '}
                {statementDetail?.status === 'Preview'
                  ? formatDateString(transactionPeriodCutOff, 'MMM d, yyyy')
                  : formatDateString(statementEndDate, 'MMM d, yyyy')}
              </Text>
            </div>

            <div>
              <Text block variant="small" className={styles.contractInfo}>
                Contract ID: <strong className={styles.value}>#{contract.id}</strong>
              </Text>
              <Text block variant="small" className={styles.contractInfo}>
                Vendor ID: <strong className={styles.value}>#{contract.vendorId}</strong>
              </Text>
            </div>
          </StackGrid>

          {tasks.length !== 0 && (
            <StatementDetailsActionRequired
              statementEndDate={statementEndDate}
              id={id}
              isLastStep={isLastStep}
              isStepBeforeBAM={isStepBeforeBAM}
              attachments={remappedAttachments}
              attachmentFileType={tasks[0].attachmentFileType}
            />
          )}

          <StackGrid horizontal gap={24} smCols={1} mdCols={2} lgCols={2}>
            <Card disableDepth className={styles.card}>
              <Text block className={styles.topText}>
                Payment
              </Text>
              <Text block className={styles.bottomText}>
                {`${formatNumberWithCommas(payment.amount.toFixed(2))} ${
                  statementDetail.payment.currency
                }`}
              </Text>
            </Card>

            <Card disableDepth className={`${styles.card} ${styles.rightCard}`}>
              <Text block className={styles.topText}>
                Payment Status
              </Text>
              <div>
                <span
                  className={
                    isPaymentSent
                      ? styles.paymentStatusSentIcon
                      : styles.paymentStatusInProgressIcon
                  }
                >
                  <FontIcon
                    iconName={isPaymentSent ? 'NavigateBackMirrored' : 'recent'}
                    arianame={isPaymentSent ? 'NavigateBackMirrored' : 'recent'}
                  />
                </span>
                <Text variant="large" className={styles.paymentStatusDate}>
                  {(() => {
                    if (payment.isPaymentCarriedOver) return 'No Payment';

                    return isPaymentSent ? 'Sent' : 'Scheduled';
                  })()}{' '}
                  {payment.isPaymentCarriedOver
                    ? ' '
                    : formatDateString(
                        isPaymentSent ? payment.paymentDate : payment.paymentDueDate,
                        'MMM d, yyyy',
                      )}
                </Text>
              </div>
              {paymentStatusInfoDetails && (
                <Text variant="tiny" className={styles.details}>
                  {PaymentStatusInfoItem({
                    paymentStatusInfoDetails,
                    invoiceDetails,
                    paymentReferenceDetails,
                  })}
                </Text>
              )}
            </Card>
          </StackGrid>
        </Header>

        <Main>
          <Module className={styles.paymentModule}>
            <ModuleHeader>Payment Breakdown</ModuleHeader>

            <ModuleContent>
              {!isSmallScreen && (
                <Pivot
                  aria-label="Royalties Earned / Payment"
                  linkSize={PivotLinkSize.large}
                  linkFormat={PivotLinkFormat.tabs}
                  styles={{
                    link: {
                      height: `${pxToRem(70)} !important`,
                      width: '50%',
                      marginRight: `${0} !important`,
                      backgroundColor: theme.palette.neutralLighterAlt,
                    },
                    linkIsSelected: {
                      height: `${pxToRem(70)} !important`,
                      width: '50%',
                      marginRight: `${0} !important`,
                      paddingLeft: pxToRem(20),
                      borderLeft: `${theme.palette.accentYellow} solid ${pxToRem(4)}`,
                      backgroundColor: theme.palette.neutralWhite,
                    },
                  }}
                >
                  {renderStatementRoyaltiesEarnedPivotItem()}
                  {renderStatementPaymentBreakdownPivotItem()}
                </Pivot>
              )}
              {isSmallScreen && (
                <>
                  <Pivot
                    aria-label="Royalties Earned"
                    linkSize={PivotLinkSize.small}
                    linkFormat={PivotLinkFormat.tabs}
                    styles={{
                      link: {
                        height: `${pxToRem(48)} !important`,
                        width: '100%',
                        backgroundColor: theme.palette.neutralLighterAlt,
                      },
                      linkIsSelected: {
                        height: `${pxToRem(48)} !important`,
                        width: '100%',
                        borderLeft: `${theme.palette.accentYellow} solid ${pxToRem(4)}`,
                        backgroundColor: theme.palette.neutralWhite,
                      },
                    }}
                  >
                    {renderStatementRoyaltiesEarnedPivotItem()}
                  </Pivot>
                  <Pivot
                    aria-label="Payment"
                    linkSize={PivotLinkSize.small}
                    linkFormat={PivotLinkFormat.tabs}
                    styles={{
                      link: {
                        height: `${pxToRem(48)} !important`,
                        width: '100%',
                        backgroundColor: theme.palette.neutralLighterAlt,
                      },
                      linkIsSelected: {
                        height: `${pxToRem(48)} !important`,
                        width: '100%',
                        borderLeft: `${theme.palette.accentYellow} solid ${pxToRem(4)}`,
                        backgroundColor: theme.palette.neutralWhite,
                      },
                    }}
                  >
                    {renderStatementPaymentBreakdownPivotItem()}
                  </Pivot>
                </>
              )}
            </ModuleContent>
          </Module>

          <Module>
            <ModuleHeader>Additional Details</ModuleHeader>
            {contractAdjustments && contractAdjustments.length > 0 && (
              <SubModule closed>
                <SubModuleHeader id="contractAdjustmentsHeading">
                  Contract Adjustments
                </SubModuleHeader>
                <SubModuleContent noPadding>
                  {contractAdjustments.map(contractAdjustment => (
                    <FinancialTable key={contractAdjustment.title}>
                      <FinancialTableHeader subHeader>
                        {contractAdjustment.title}
                      </FinancialTableHeader>
                      <FinancialTableBody
                        items={contractAdjustment.balanceBreakdown?.map(breakdownMapping)}
                        collapsibleSubLineItems
                      />
                    </FinancialTable>
                  ))}
                </SubModuleContent>
              </SubModule>
            )}

            {tieringBreakdown && (
              <SubModule closed>
                <SubModuleHeader id="tieringAndFeeStructure">
                  Tiering and Fee Structure
                </SubModuleHeader>
                <SubModuleContent>
                  {tieringBreakdown.summary.map(summary => (
                    <StatementTieringAndFeeStructure {...summary} />
                  ))}
                </SubModuleContent>
              </SubModule>
            )}

            {/* <SubModule closed>
              <SubModuleHeader>Quantity Deduction Cap</SubModuleHeader>
            </SubModule> */}

            {comments && comments.length > 0 && (
              <SubModule closed>
                <SubModuleHeader id="comments">Comments</SubModuleHeader>
                <SubModuleContent>
                  {comments.map(comment => (
                    <Stack horizontal style={{ marginBottom: '1.5em' }}>
                      <Stack.Item style={{ marginRight: '1.5em' }}>
                        <FontIcon aria-label="comment" iconName="Comment" arianame="comment" />
                      </Stack.Item>
                      <Stack.Item>
                        <Stack style={{ marginBottom: '0' }}>
                          <Stack.Item>
                            {comment.id && (
                              <Text
                                style={{
                                  marginRight: '0.75em',
                                  verticalAlign: 'super',
                                  fontWeight: 400,
                                  fontSize: '0.75em',
                                }}
                              >
                                {comment.id}
                              </Text>
                            )}

                            {comment.user && (
                              <Text>
                                {comment.user} <Text style={{ fontWeight: 400 }}>commented</Text>
                              </Text>
                            )}

                            {comment.header && (
                              <Link
                                aria-label="adjustment name"
                                href={`/adjustments/${comment.adjustmentId}`}
                              >
                                {comment.header}
                              </Link>
                            )}
                          </Stack.Item>
                          <Stack.Item style={{ margineTop: '1em' }}>
                            {comment.content.map(x => (
                              <>
                                <Text style={{ fontWeight: 250, paddingLeft: 50 }}>{x}</Text>
                                <br />
                              </>
                            ))}
                          </Stack.Item>
                          <Stack.Item style={{ marginTop: '1em' }}>
                            {comment.date && (
                              <Text variant="small">
                                {moment.utc(comment.date).format('MMM DD YYYY HH:MM A')}
                              </Text>
                            )}
                          </Stack.Item>
                        </Stack>
                      </Stack.Item>
                    </Stack>
                  ))}
                </SubModuleContent>
              </SubModule>
            )}

            {!statementDetail?.config?.excludeSections?.includes('ComparisonData') && (
              <SubModule closed>
                <SubModuleHeader id="comparisonDataHeading">Comparison Data</SubModuleHeader>
                <SubModuleContent>
                  <ComparisonTable items={statementDetail} />
                </SubModuleContent>
              </SubModule>
            )}

            {perCapDetails && perCapDetails.length > 0 && (
              <SubModule closed>
                <SubModuleHeader id="quantityDeductionCap">
                  <div>
                    Quantity Deduction Cap
                    <TooltipHost
                      content="Quantity deduction cap used to determine whether or not a return will impact royalty earnings. The quantity deduction cap is calculated at the end of each statement period and is based on the purchases and returns from the fixed fiscal quarter"
                      calloutProps={{
                        target: '#deductionCapIcon',
                      }}
                    >
                      <FontIcon
                        tabIndex="0"
                        aria-label="Info"
                        iconName="Info"
                        arianame="Info"
                        className={styles.percapInfoIcon}
                        id="deductionCapIcon"
                      />
                    </TooltipHost>
                  </div>
                </SubModuleHeader>
                <SubModuleContent>
                  {perCapDetails && perCapDetails.length > 0 && (
                    <FinancialTable>
                      <Text block className={styles.percapText}>
                        {'This period had '}
                        <strong>{`${sumBy(
                          perCapDetails,
                          item => item?.currentStatementPeriod?.returnsIncluded,
                        )}`}</strong>
                        {' allowed returns, totalling '}
                        <strong>{`${roundedSum(perCapDetails)} ${
                          perCapDetails?.[0]?.contractCurrency
                        }.`}</strong>
                      </Text>
                      <FinancialTableBodyHeader
                        name="Timeframe"
                        purchasedUnits="Purchased Units"
                        returnCap="Return Cap"
                        capLimit="Cap Limit"
                        actualReturns="Actual Returns"
                        returnsOverCap="Returns Over Cap"
                        returnsIncluded="Returns Included"
                        amount="Amount Returned"
                        isPerCap
                      />
                      <FinancialTableBody
                        items={perCapDetails?.map(item => ({
                          name: item.quarter,
                          amount: item.amountReturned,
                          details: [
                            { ...item.previousStatementSnapShot, name: 'Prev. Statement Snapshot' },
                            { ...item.currentStatementPeriod, name: 'Current Statement Period' },
                          ],
                          parenthesizeDetails: true,
                          currency: item.contractCurrency,
                          returnCap: item.returnCap,
                          capLimit: item.capLimit,
                          actualReturns: item.actualReturns,
                          returnsOverCap: item.returnsOverCap,
                          returnsIncluded: item.returnsIncluded,
                          purchasedUnits: item.purchasedUnits,
                          isPerCap: true,
                        }))}
                      />
                      <FinancialTableFooter
                        name="Total Current Statement Returns"
                        amount={sumBy(
                          perCapDetails,
                          item => item?.currentStatementPeriod?.amountReturned,
                        )}
                        returnsIncluded={sumBy(
                          perCapDetails,
                          item => item?.currentStatementPeriod?.returnsIncluded,
                        )}
                        currency={perCapDetails?.[0]?.contractCurrency}
                        isPerCap
                      />
                    </FinancialTable>
                  )}
                </SubModuleContent>
              </SubModule>
            )}

            {canViewStatementAttachments && (
              <SubModule closed>
                <SubModuleHeader id="attachmentsHeading">Attachments</SubModuleHeader>
                <SubModuleContent>
                  {remappedAttachments && remappedAttachments.length > 0 && (
                    <div className={styles.remappedAttachments}>
                      <DocumentsList
                        items={remappedAttachments}
                        actionButtons={[canDeleteStatementAttachments ? 'remove' : '', 'download']}
                        isHeaderVisible={false}
                        downloadDocument={handleDownload}
                        deleteDocument={handleDelete}
                      />
                    </div>
                  )}
                </SubModuleContent>
              </SubModule>
            )}
          </Module>

          {canViewStatementActivity && workFlowSteps.length > 0 && (
            <>
              <Module>
                <ModuleHeader className={styles.statementActivity}>Statement Activity</ModuleHeader>

                <ModuleContent
                  style={{
                    background: 'transparent',
                    boxShadow: 'none',
                  }}
                >
                  <VerticalSteps
                    steps={workFlowSteps}
                    reconResults={statementDetail?.reconResults}
                  />
                </ModuleContent>
              </Module>
            </>
          )}

          {can('viewCustomerSupportInfo') && (
            <Module>
              <ContactModule
                title="Questions?"
                body="Contact Royalty and Content Operations via email for any questions regarding your statement."
                mail="roycare@microsoft.com"
              />
              <div className={styles.footerText}>
                <Text variant="small">Statement prepared by Royalty and Content Operations</Text>
              </div>
            </Module>
          )}
        </Main>
      </>
    )
  );
};

Statement.propTypes = {
  directionalHint: PropTypes.number,
};
Statement.defaultProps = {
  directionalHint: DirectionalHint.topCenter,
};
export default Statement;
